import type { OddsPreference } from "@/features/betslip/types";
import type {
  BetslipActions,
  BetslipStore,
  SliceCreator,
} from "@/features/betslip/stores/types";
import {
  computeIsPlacing,
  isPlacedOutcome,
  selectCanViewLiveMarkets,
  selectMultiLegs,
} from "@/features/betslip/stores/computed";
import {
  clearAcceptedForEdit,
  exitConfirmingIfMutated,
  omitKey,
} from "@/features/betslip/stores/slices/submission";

type UpdatesState = Pick<
  BetslipStore,
  | "oddsChanges"
  | "semRedundantLegs"
  | "marketStatuses"
  | "marketBettingTypes"
  | "eventStatuses"
>;

export const initialUpdatesState: UpdatesState = {
  oddsChanges: {},
  semRedundantLegs: {},
  marketStatuses: {},
  marketBettingTypes: {},
  eventStatuses: {},
};

export const pruneUpdatesAfterRemoval = (
  state: BetslipStore,
  keepOutcomeIds: ReadonlySet<string>,
  // Event-keyed, so they only go once nothing references the event at all.
  // Omit to keep them all.
  keepEventIds?: ReadonlySet<string>,
): UpdatesState => {
  const nextChanges: BetslipStore["oddsChanges"] = {};
  for (const [outcomeId, value] of Object.entries(state.oddsChanges)) {
    if (keepOutcomeIds.has(outcomeId) && value !== undefined) {
      nextChanges[outcomeId] = value;
    }
  }
  const nextRedundantLegs: BetslipStore["semRedundantLegs"] = {};
  for (const [outcomeId, value] of Object.entries(state.semRedundantLegs)) {
    if (keepOutcomeIds.has(outcomeId) && value !== undefined) {
      nextRedundantLegs[outcomeId] = value;
    }
  }
  const nextStatuses: BetslipStore["marketStatuses"] = {};
  for (const [outcomeId, value] of Object.entries(state.marketStatuses)) {
    if (keepOutcomeIds.has(outcomeId) && value !== undefined) {
      nextStatuses[outcomeId] = value;
    }
  }
  const nextBettingTypes: BetslipStore["marketBettingTypes"] = {};
  for (const [outcomeId, value] of Object.entries(state.marketBettingTypes)) {
    if (keepOutcomeIds.has(outcomeId) && value !== undefined) {
      nextBettingTypes[outcomeId] = value;
    }
  }

  let nextEventStatuses = state.eventStatuses;
  if (keepEventIds) {
    nextEventStatuses = {};
    for (const [eventId, value] of Object.entries(state.eventStatuses)) {
      if (keepEventIds.has(eventId) && value !== undefined) {
        nextEventStatuses[eventId] = value;
      }
    }
  }

  return {
    oddsChanges: nextChanges,
    semRedundantLegs: nextRedundantLegs,
    marketStatuses: nextStatuses,
    marketBettingTypes: nextBettingTypes,
    eventStatuses: nextEventStatuses,
  };
};

type UpdatesActions = Pick<
  BetslipActions,
  | "setOddsChange"
  | "discardOddsChange"
  | "acceptOddsChange"
  | "acceptAllOddsChanges"
  | "acceptMultiOddsChanges"
  | "setSemRedundantLegs"
  | "setMarketStatus"
  | "setMarketBettingType"
  | "setEventStatus"
>;

const shouldAutoAcceptOddsChange = (
  preference: OddsPreference,
  newOdds: number,
  currentOdds: number,
): boolean => {
  if (preference === "AUTO") return true;
  if (preference === "AUTO_HIGHER") return newOdds > currentOdds;
  return false;
};

export const updatesSlice: SliceCreator<
  typeof initialUpdatesState & UpdatesActions
> = (set, get) => ({
  ...initialUpdatesState,

  setOddsChange: (outcomeId, newOdds, preference) => {
    set((state) => {
      // A placed card is a receipt, not a live price: it has to keep showing
      // the price its bet actually went on at. useOddsSync already holds
      // ticks while the slip is in review; this is the store side of that
      // rule. Nothing is lost by dropping the tick — editing or reusing the
      // slip clears the results, the sync's pause lifts, and the live price
      // lands again on the next observation. Selections that took no part in
      // the placed bet still track the market as usual.
      if (isPlacedOutcome(state, outcomeId)) return state;

      const selection = state.selections[outcomeId];
      if (!selection) return state;

      // Price is back at the committed value. If a change was pending it's
      // moot now — drop it so the "Odds Updated" alert clears. Otherwise
      // there's nothing to write.
      if (selection.odds === newOdds) {
        if (state.oddsChanges[outcomeId] === undefined) return state;
        const { [outcomeId]: _, ...remainingChanges } = state.oddsChanges;
        return {
          ...exitConfirmingIfMutated(state),
          oddsChanges: remainingChanges,
        };
      }

      // A zero-odds selection has no real price yet — the first tick is
      // a seed, not a change, so it applies silently regardless of the
      // user's accept-odds preference.
      const isFirstPrice = selection.odds === 0;

      const autoAccept = shouldAutoAcceptOddsChange(
        preference,
        newOdds,
        selection.odds,
      );

      if (isFirstPrice || autoAccept) {
        // Bake the new price into the selection. Drop any pending entry
        // since this change supersedes it.
        const { [outcomeId]: _, ...remainingChanges } = state.oddsChanges;
        return {
          ...exitConfirmingIfMutated(state),
          oddsChanges: remainingChanges,
          selections: {
            ...state.selections,
            [outcomeId]: { ...selection, odds: newOdds },
          },
        };
      }

      return {
        ...exitConfirmingIfMutated(state),
        oddsChanges: { ...state.oddsChanges, [outcomeId]: newOdds },
      };
    });
  },

  discardOddsChange: (outcomeId) => {
    set((state) => {
      if (state.oddsChanges[outcomeId] === undefined) return state;
      const { [outcomeId]: _, ...remainingChanges } = state.oddsChanges;
      return {
        ...exitConfirmingIfMutated(state),
        oddsChanges: remainingChanges,
      };
    });
  },

  acceptOddsChange: (outcomeId) => {
    set((state) => {
      // Mid-submit the reuse path below would wipe an outstanding bet's
      // receipt, and baking a price is moot until the submit lands.
      if (computeIsPlacing(state)) return state;

      const selection = state.selections[outcomeId];
      if (!selection) return state;

      // Two separate things bring the user here: a pending price change, and
      // an odds.changed rejection to clear.
      const pendingOdds = state.oddsChanges[outcomeId];
      const result = state.selectionResults[outcomeId];
      const singleRejectedOnOdds =
        result?.status === "rejected" &&
        result.rejection.reason === "odds.changed";
      const legRejectedOnOdds =
        state.multiLegRejections[outcomeId]?.reason === "odds.changed";

      // No price and no rejection = nothing to accept.
      if (
        pendingOdds === undefined &&
        !singleRejectedOnOdds &&
        !legRejectedOnOdds
      ) {
        return state;
      }

      const cleared = clearAcceptedForEdit(state);

      // use the pending price if we have it
      const selections =
        pendingOdds === undefined
          ? state.selections
          : {
              ...state.selections,
              [outcomeId]: { ...selection, odds: pendingOdds },
            };

      // Clear whichever rejection was ours. The other map goes back untouched.
      const selectionResults = singleRejectedOnOdds
        ? omitKey(cleared.selectionResults, outcomeId)
        : cleared.selectionResults;
      const multiLegRejections = legRejectedOnOdds
        ? omitKey(state.multiLegRejections, outcomeId)
        : state.multiLegRejections;

      return {
        ...exitConfirmingIfMutated(state),
        ...cleared,
        selections,
        selectionResults,
        multiLegRejections,
        oddsChanges: omitKey(state.oddsChanges, outcomeId),
      };
    });
  },

  acceptAllOddsChanges: (preference) => {
    set((state) => {
      if (computeIsPlacing(state)) return state;

      const pendingIds = Object.keys(state.oddsChanges);
      if (pendingIds.length === 0) return state;

      const nextSelections = { ...state.selections };
      const nextChanges = { ...state.oddsChanges };
      const oddsChanges: BetslipStore["oddsChanges"] = {};
      for (const outcomeId of pendingIds) {
        const selection = nextSelections[outcomeId];
        const newOdds = state.oddsChanges[outcomeId];
        if (!selection || newOdds === undefined) continue;
        // AUTO_HIGHER only accepts increases; skip any drop. Undefined
        // (explicit "Accept All") or AUTO accepts everything.
        if (preference === "AUTO_HIGHER" && newOdds < selection.odds) continue;
        nextSelections[outcomeId] = { ...selection, odds: newOdds };
        delete nextChanges[outcomeId];
        oddsChanges[outcomeId] = newOdds;
      }
      if (Object.keys(oddsChanges).length === 0) return state;

      const cleared = clearAcceptedForEdit(state);

      // Taking a price answers the odds.changed rejection that came with it.
      // Leaving it would strand the card with a banner for a price it already
      // committed, and the slip un-submittable.
      const selectionResults: BetslipStore["selectionResults"] = {};
      for (const [outcomeId, result] of Object.entries(
        cleared.selectionResults,
      )) {
        const shouldClear =
          oddsChanges[outcomeId] !== undefined &&
          result.status === "rejected" &&
          result.rejection.reason === "odds.changed";
        if (!shouldClear) selectionResults[outcomeId] = result;
      }
      const multiLegRejections: BetslipStore["multiLegRejections"] = {};
      for (const [outcomeId, rejection] of Object.entries(
        state.multiLegRejections,
      )) {
        const shouldClear =
          oddsChanges[outcomeId] !== undefined &&
          rejection.reason === "odds.changed";
        if (!shouldClear) multiLegRejections[outcomeId] = rejection;
      }

      return {
        ...exitConfirmingIfMutated(state),
        ...cleared,
        selectionResults,
        multiLegRejections,
        oddsChanges: nextChanges,
        selections: nextSelections,
      };
    });
  },

  // The multi/combo accept needs to go through its legs and accept
  acceptMultiOddsChanges: () => {
    for (const leg of selectMultiLegs(get())) {
      get().acceptOddsChange(leg.outcomeId);
    }
  },

  setSemRedundantLegs: (outcomeId, legOutcomeIds) => {
    set((state) => {
      const current = state.semRedundantLegs[outcomeId] ?? [];
      const unchanged =
        current.length === legOutcomeIds.length &&
        current.every((legOutcomeId) => legOutcomeIds.includes(legOutcomeId));
      if (unchanged) return state;

      const next =
        legOutcomeIds.length === 0
          ? omitKey(state.semRedundantLegs, outcomeId)
          : { ...state.semRedundantLegs, [outcomeId]: legOutcomeIds };
      return { semRedundantLegs: next };
    });
  },

  setMarketStatus: (outcomeId, status) => {
    set((state) => {
      const current = state.marketStatuses[outcomeId];
      const target = status === "ACTIVE" ? undefined : status;
      if (current === target) return state;

      const next =
        target === undefined
          ? omitKey(state.marketStatuses, outcomeId)
          : { ...state.marketStatuses, [outcomeId]: target };
      // A status change alters the bet on offer (a scratched runner drops out
      // of the confirm), so back out of the confirm prompt the same way an
      // odds change does — otherwise the slip can sit in a confirm with its
      // only selection filtered away.
      return { ...exitConfirmingIfMutated(state), marketStatuses: next };
    });
  },

  setMarketBettingType: (outcomeId, bettingType) => {
    set((state) => {
      const current = state.marketBettingTypes[outcomeId];
      if (current === bettingType) return state;

      const next = {
        ...state.marketBettingTypes,
        [outcomeId]: bettingType,
      };
      // Ignorable only if it can't turn the selection view-only.
      const canIgnoreChange =
        bettingType !== "Live" || selectCanViewLiveMarkets(state);
      if (current === undefined && canIgnoreChange) {
        return { marketBettingTypes: next };
      }

      return { ...exitConfirmingIfMutated(state), marketBettingTypes: next };
    });
  },

  // Event-keyed, so every selection on the event shares one entry.
  setEventStatus: (eventId, status) => {
    set((state) => {
      const current = state.eventStatuses[eventId];
      if (current === status) return state;

      const next = { ...state.eventStatuses, [eventId]: status };
      // Ignorable only if it can't turn selections view-only.
      const canIgnoreChange =
        status !== "LIVE" || selectCanViewLiveMarkets(state);
      if (current === undefined && canIgnoreChange) {
        return { eventStatuses: next };
      }

      return { ...exitConfirmingIfMutated(state), eventStatuses: next };
    });
  },
});
